<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import BarChart from './smbgCompliance/BarChart.svelte';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faChevronUp, faChevronDown, faRotate } from '@fortawesome/pro-light-svg-icons';
  const API_BASE_URL = '/api/proxy';

  import { exportToCSV, exportToExcel, generateExportFilename } from '$utils/export.js';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';

  let compliances = [];
  let compliancesCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let chartCompliances = [];
  let isLoading = true;
  let chartVisible = false;

  const requiredTypes = ['BEDTIME', 'EVENING', 'MIDDAY', 'MORNING'];

  function hasRequiredTypes(types) {
    const result = requiredTypes.every((required) => types.some(({ type }) => type === required));

    // console.log('Types Check:', {
    //   incoming: types,
    //   required: requiredTypes,
    //   hasAll: result,
    //   missing: requiredTypes.filter(required =>
    //     !types.some(({ type }) => type === required)
    //   )
    // });

    return result;
  }

  async function fetchCompliances(page = 1) {
    try {
      isLoading = true;
      const res = await fetch(
        `${API_BASE_URL}/glucoseReadings/daily?page=${page}&pageSize=${limit}&subjectGUID=${$selectedParticipant._id}&type=SMBG&timezone=${$selectedParticipant.timezoneCode}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch cgm compliance');
      }

      const data = await res.json();
      //console.info("data: ", data);

      compliances = data.data || []; // Ensure compliances is an array
      compliancesCount = data.totalCount || 0;
      currentPage = data.page || 1;
      totalPages = data.totalPages || 1;
    } catch (err) {
      console.error('Error fetching compliance:', err);
      compliances = []; // Set to empty array in case of error
    } finally {
      isLoading = false;
    }
  }

  async function fetchChartCompliances() {
    try {
      const res = await fetch(
        `${API_BASE_URL}/glucoseReadings/dailyChart?page=1&pageSize=224&subjectGUID=${$selectedParticipant._id}&type=SMBG&timezone=${$selectedParticipant.timezoneCode}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch cgm compliance');
      }

      const data = await res.json();
      chartCompliances = data.data || [];
      //chartCompliances = [...chartCompliances].reverse();
    } catch (err) {
      console.error('Error fetching compliance:', err);
      compliances = []; // Set to empty array in case of error
    }
  }

  async function fetchAllCompliances() {
    try {
      const res = await fetch(
        `${API_BASE_URL}/glucoseReadings/daily?page=1&pageSize=1000&subjectGUID=${$selectedParticipant._id}&type=SMBG&timezone=${$selectedParticipant.timezoneCode}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch all cgm compliance');
      }

      return await res.json();
    } catch (err) {
      console.error('Error fetching all compliance:', err);
      return [];
    }
  }

  onMount(() => {
    if ($selectedParticipant) {
      fetchCompliances(1);
      fetchChartCompliances();
    } else {
      isLoading = false;
    }
  });

  async function handleExportCSV() {
    const allCompliances = await fetchAllCompliances();
    exportToCSV(allCompliances, 'compliances');
  }

  async function handleExportExcel() {
    const allCompliances = await fetchAllCompliances();
    
    const filename = generateExportFilename({
      protocolNumber: $selectedStudy?.protocolNumber,
      subjectId: $selectedParticipant?.subjectId,
      reportType: 'SMBG_Compliance'
    });
    
    exportToExcel(allCompliances, filename);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchCompliances(newPage);
    }
  }

  function handleRefresh() {
    //console.log("handleRefresh");
    fetchCompliances(1);
  }

  function handleChartVisibility() {
    //console.log("handleChartVisibility");
    chartVisible = !chartVisible;
  }
</script>

<StudyHeader />

<FeatureInfo 
  title="SMBG Compliance" 
  description="This page displays self-monitored blood glucose (SMBG) fingerstick data for the selected participant. You can view daily readings, check if all required readings (morning, midday, evening, bedtime) were taken, and track compliance over time."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

<Toolbar title="SMBG Compliance" itemCountTitle="Days" itemCount={compliancesCount}>
  <div>
    <button on:click={handleChartVisibility} class="pagination mx-1 p-0 text-sm" title="Toggle daily chart visibility">
      Daily Chart
      {#if chartVisible}
        <FontAwesomeIcon icon={faChevronDown} />
      {:else}
        <FontAwesomeIcon icon={faChevronUp} />
      {/if}
    </button>
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
      preparing SMBG compliance data
    </p>
  </div>
{:else}
  {#if chartVisible}
    <!-- Bar Chart -->
    {#if chartCompliances.length > 0}
      <div class="flex pb-5">
        <BarChart data={chartCompliances} />
      </div>
    {/if}
  {/if}

  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Day (Local)</th>
          <th class="px-4 py-2 text-left bold">Serial Nr</th>
          <th class="px-4 py-2 text-left bold">Total</th>
          <th class="px-4 py-2 text-left bold">Types</th>
          <th class="px-4 py-2 text-left bold">First (Local)</th>
          <th class="px-4 py-2 text-left bold">Last (Local)</th>
        </tr>
      </thead>
      <tbody>
        {#each compliances as compliance, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{compliance.day}</td>
            <td class="px-4 py-2">{compliance.externalId}</td>
            <td
              class="px-4 py-2 {!hasRequiredTypes(compliance.types)
                ? 'text-gray-700 dark:text-slate-200 font-semibold'
                : ''}"
            >
              {compliance.totalCount}
            </td>
            <td class="px-4 py-2">
              {#if compliance.types && compliance.types.length > 0}
                {compliance.types
                  .sort((a, b) => a.type.localeCompare(b.type))
                  .map(
                    ({ type, count }) =>
                      `${type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()}: ${count}`
                  )
                  .join(', ')}
              {/if}
            </td>
            <td class="px-4 py-2"
              >{formatToLocalDateTime(compliance.minTimestamp, $selectedParticipant.timezoneCode)}</td
            >
            <td class="px-4 py-2"
              >{formatToLocalDateTime(compliance.maxTimestamp, $selectedParticipant.timezoneCode)}</td
            >
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<!-- Pagination and Export Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      hidden
      on:click={handleExportCSV}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >CSV</button
    >
    <button
      hidden
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
